import { skipToken } from "@reduxjs/toolkit/query";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { isNotificationLoadIssue, NotificationLoadIssue } from "notifications/_stores/notifications-redux-slice";
import type { NotificationType } from "notifications/notification-type";
import TopicCategory from "notifications/topics/topic-category";
import { useMemo } from "react";
import { useGetProposalsByIdsQuery } from "work/proposals/_api/proposal-api";
import type BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import { getProposalStatusLabel } from "work/proposals/proposal-status-display";
import WorkVersion from "work/work-agreements/versions/work-version";

type NotificationPage = PaginatedResponse<NotificationLoadIssue | NotificationType> | undefined;

/** The two things a row can say about the proposal it is about: which one it is, and where it stands. */
export type NotificationProposal = Readonly<{
  /** What the proposal is called now, which is not necessarily what it was called when the row was sent. */
  name?: string;
  /**
   * The version of the work it creates, "v2 (proposed)", or "v2" once hired. Apart from the name so
   * the row can cut a long name short and still show it: a change order keeps its project's name
   * (#6809).
   */
  version?: string;
  /** The stage it is at, spelled for a reader. */
  statusLabel?: string;
}>;

export type NotificationProposalDetails = Readonly<{
  /** The proposals that came back, keyed by proposal id. */
  byId: ReadonlyMap<string, NotificationProposal>;
  /** Whether they are still on their way. */
  isLoading: boolean;
}>;

/**
 * The proposal each of the given notifications is about — its name and its current stage — keyed by
 * proposal id.
 *
 * A proposal notification says what happened at the moment it was sent, not where the proposal
 * stands now — and by the time it is read the proposal has usually moved on. The rows name their
 * proposal by topic id alone, so the proposals are fetched for the page being shown, the way the
 * sender and recipient names on the same page already are (#5129).
 *
 * Nothing is requested for a page with no proposal rows on it, which is most of them.
 */
export default function useNotificationProposals(
  tableNotifications: NotificationPage,
): NotificationProposalDetails {
  const proposalIds = useMemo(() => getNotificationProposalTopicIds(tableNotifications), [tableNotifications]);
  const proposalQuery = useGetProposalsByIdsQuery(proposalIds.length ? { proposalIds } : skipToken);
  const byId = useMemo(() => getNotificationProposalsById(proposalQuery.data?.found), [proposalQuery.data]);

  // Skipped queries never report loading, so a page with no proposal rows on it is not "waiting".
  return useMemo(
    () => ({ byId, isLoading: proposalQuery.isLoading }),
    [byId, proposalQuery.isLoading],
  );
}

/**
 * The proposals the rows on screen are about, deduplicated. Rows that failed to load, and rows
 * whose topic is not a proposal, name nothing to fetch.
 */
export function getNotificationProposalTopicIds(tableNotifications: NotificationPage): string[] {
  if (!tableNotifications) return [];

  const proposalIds = new Set<string>();
  tableNotifications.data.forEach((notification) => {
    if (isNotificationLoadIssue(notification)) return;
    if (notification.topic?.category !== TopicCategory.Work.Proposal) return;

    const topicId = notification.topic?.id?.value;
    if (topicId) proposalIds.add(topicId);
  });

  return [...proposalIds];
}

/**
 * The name and stage of each proposal that came back, spelled for a reader. A proposal the reader
 * may not open arrives as an error carrying neither, and so contributes nothing — a row says which
 * proposal it is about and where it stands only where the proposal itself could have been opened.
 */
export function getNotificationProposalsById(
  proposals: BarebonesProposalResponse[] | undefined,
): ReadonlyMap<string, NotificationProposal> {
  const byId = new Map<string, NotificationProposal>();

  for (const proposal of proposals ?? []) {
    if (!proposal.id) continue;

    const name = proposal.name || undefined;
    const version = name ? WorkVersion.fromNumbers(proposal)?.label : undefined;
    const statusLabel = getProposalStatusLabel(proposal.status);
    if (!name && !statusLabel) continue;

    byId.set(proposal.id, { name, version, statusLabel });
  }

  return byId;
}
